/**
 * features/attendance/AbsentToday.tsx
 * ------------------------------------------------------------------
 * Round 13 — "Absent today" daily-ops view.
 *
 * Visible to: admin + headteacher + HOY.
 * Parent phone numbers shown to: admin + HOY only (decision Q2=opt1).
 * 'Today' meaning: strict actual today; if no entries yet, show
 *   empty-state explaining the situation (decision Q3=opt 'narrative').
 *
 * Pulls every reportDailyAttendance doc dated today, then lists
 * students whose status is 'absent' (also: sick + excused shown
 * separately for context — leadership cares about all absences,
 * but the count headline focuses on 'absent' specifically).
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { AlertCircle, Phone, MessageSquare, Mail } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { Spinner, Card, Badge } from '@/components/ui/primitives';
import { GradientHero } from '@/components/ui/glass';
import type { DailyAttendanceDoc, AttendanceStatus } from '@/types';

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatToday(): string {
  return new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export default function AbsentToday() {
  const { activeStudents: students, classes } = useData();
  const { userData } = useAuth();
  const roles = userData?.roles?.length ? userData.roles : (userData?.role ? [userData.role] : []);
  const canSeePhone = roles.includes('admin') || roles.includes('hoy');

  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState<DailyAttendanceDoc[]>([]);
  const [error, setError] = useState<string | null>(null);

  const today = todayISO();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        // Query only today's docs — efficient since reportDailyAttendance
        // is keyed by classId+date; if there are many days, we don't
        // want to scan them all.
        const q = query(
          collection(db, COLLECTIONS.dailyAttendance),
          where('date', '==', today),
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        const all: DailyAttendanceDoc[] = snap.docs.map(
          (d) => ({ id: d.id, ...d.data() }) as DailyAttendanceDoc,
        );
        setDocs(all);
        setError(null);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [today]);

  /* ---- collect absentees ---- */
  interface AbsenteeRow {
    studentId: string;
    studentName: string;
    classId: string;
    className: string;
    status: AttendanceStatus;
    guardianName?: string;
    guardianPhone?: string;
    guardianEmail?: string;
  }

  const absentees = useMemo<AbsenteeRow[]>(() => {
    const rows: AbsenteeRow[] = [];
    for (const d of docs) {
      const cls = classes.find((c) => c.id === d.classId);
      const className = cls?.name ?? d.classId;
      for (const [sid, status] of Object.entries(d.entries) as [string, AttendanceStatus][]) {
        if (status === 'absent' || status === 'sick' || status === 'excused') {
          const student = students.find((s) => s.id === sid);
          if (!student) continue;
          rows.push({
            studentId: sid,
            studentName: student.fullName,
            classId: d.classId,
            className,
            status,
            guardianName: student.parentContact?.guardianName,
            guardianPhone: student.parentContact?.whatsapp,
            guardianEmail: student.parentContact?.email,
          });
        }
      }
    }
    // Sort by class then by status priority (absent first), then by name
    const statusOrder = { absent: 0, sick: 1, excused: 2 } as Record<string, number>;
    rows.sort((a, b) => {
      if (a.className !== b.className) return a.className.localeCompare(b.className);
      const s = (statusOrder[a.status] ?? 9) - (statusOrder[b.status] ?? 9);
      if (s !== 0) return s;
      return a.studentName.localeCompare(b.studentName);
    });
    return rows;
  }, [docs, students, classes]);

  // SICK is regarded as absent (policy Aug 2026): the Absent headline includes
  // sick; the Sick box below is shown as a sub-count of that total.
  const absentCount = absentees.filter((a) => a.status === 'absent' || a.status === 'sick').length;
  const sickCount = absentees.filter((a) => a.status === 'sick').length;
  const excusedCount = absentees.filter((a) => a.status === 'excused').length;
  const classesRecorded = docs.length;

  if (loading) {
    return (
      <div className="p-8">
        <Spinner label="Loading today's attendance…" />
      </div>
    );
  }
  if (error) {
    return (
      <Card className="p-4 m-4 text-red-700 bg-red-50 border-red-200">
        <p className="font-medium">Failed to load attendance.</p>
        <p className="text-sm mt-1">{error}</p>
      </Card>
    );
  }

  /* ---- empty state — no entries today ---- */
  if (docs.length === 0) {
    return (
      <div className="space-y-4 px-1">
        <GradientHero
          title="Absent today"
          subtitle={formatToday()}
          icon={AlertCircle}
        />
        <Card className="p-8 text-center">
          <AlertCircle size={36} className="mx-auto text-gray-400 mb-3" />
          <p className="text-base font-medium text-gray-700 dark:text-gray-200">
            No attendance recorded today yet.
          </p>
          <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
            Class teachers haven't marked attendance for {formatToday().split(',')[0]} yet.
            Once they do, the absentees will appear here automatically.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 px-1">
      <GradientHero
        title="Absent today"
        subtitle={`${formatToday()} · ${classesRecorded} class${classesRecorded === 1 ? '' : 'es'} recorded`}
        icon={AlertCircle}
      />

      {/* Headline stats */}
      <div className="grid grid-cols-3 gap-3">
        <StatBox label="Absent (incl. sick)" value={absentCount} tone="red" />
        <StatBox label="of which sick" value={sickCount} tone="purple" />
        <StatBox label="Excused" value={excusedCount} tone="blue" />
      </div>

      {/* Absentee list */}
      {absentees.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-emerald-700 dark:text-emerald-300 font-semibold text-lg">
            Full attendance today across {classesRecorded} class
            {classesRecorded === 1 ? '' : 'es'}.
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Every recorded student is present or late.
          </p>
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left">
              <tr>
                <th className="px-4 py-2 font-semibold text-gray-700 dark:text-gray-200">Student</th>
                <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-200">Class</th>
                <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-200">Status</th>
                <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-200">Guardian</th>
                {canSeePhone && (
                  <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-200">Contact</th>
                )}
              </tr>
            </thead>
            <tbody>
              {absentees.map((r, idx) => (
                <tr
                  key={`${r.classId}_${r.studentId}`}
                  className={
                    idx % 2 === 0
                      ? 'bg-white dark:bg-gray-900'
                      : 'bg-gray-50/60 dark:bg-gray-800/30'
                  }
                >
                  <td className="px-4 py-2 font-medium">{r.studentName}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{r.className}</td>
                  <td className="px-3 py-2">
                    <StatusPill status={r.status} />
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                    {r.guardianName ?? <span className="text-gray-400 italic">unknown</span>}
                  </td>
                  {canSeePhone && (
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-3 text-xs">
                        {r.guardianPhone ? (
                          <a
                            href={`tel:${r.guardianPhone}`}
                            className="flex items-center gap-1 text-blue-600 dark:text-blue-300 hover:underline"
                          >
                            <Phone size={12} />
                            {r.guardianPhone}
                          </a>
                        ) : (
                          <span className="text-gray-400 italic">no phone</span>
                        )}
                        {r.guardianPhone && (
                          <a
                            href={`https://wa.me/${r.guardianPhone.replace(/[^0-9+]/g, '')}`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-1 text-emerald-600 dark:text-emerald-300 hover:underline"
                            title="WhatsApp"
                          >
                            <MessageSquare size={12} />
                          </a>
                        )}
                        {r.guardianEmail && (
                          <a
                            href={`mailto:${r.guardianEmail}`}
                            className="flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:underline"
                            title={r.guardianEmail}
                          >
                            <Mail size={12} />
                          </a>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {!canSeePhone && (
        <p className="text-xs text-gray-500 italic px-2">
          Parent contact details are visible to admin and HOY only. Click into a
          student profile for further details.
        </p>
      )}
    </div>
  );
}

/* ---------------- subcomponents ---------------- */
function StatBox({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'red' | 'purple' | 'blue';
}) {
  const classes: Record<typeof tone, string> = {
    red: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-200',
    purple: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-200',
    blue: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200',
  };
  return (
    <Card className={`p-4 ${classes[tone]}`}>
      <p className="text-[11px] uppercase tracking-wide font-semibold opacity-80">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
    </Card>
  );
}

function StatusPill({ status }: { status: AttendanceStatus }) {
  const labels: Record<AttendanceStatus, string> = {
    present: 'Present',
    absent: 'Absent',
    late: 'Late',
    excused: 'Excused',
    sick: 'Sick',
  };
  const classes: Record<AttendanceStatus, string> = {
    present: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-700',
    absent: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-700',
    late: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-700',
    excused: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-700',
    sick: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-200 dark:border-purple-700',
  };
  return <Badge className={classes[status]}>{labels[status]}</Badge>;
}
